import { useState, useMemo } from 'react';
import {
  Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Button, required, useTranslate } from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import { GraySelectInputWithUpperLabel } from './inputs/GraySelectInputWithUpperLabel';
import type { CertificationDocument, DocumentAssignee } from '../../../utils/certificationDocuments';
import { DocumentaryProofPreview } from './DocumentaryProof';

const DocumentPreviewButton = (props: { document: CertificationDocument }) => {

  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const previewSrc = props.document.src;
  if (!previewSrc) return null;
  const fileName = props.document.title ?? props.document.rawFile?.name;

  return (
    <>
      <Button
        startIcon={<AttachFileIcon />}
        onClick={() => setOpen(true)}
        variant="contained"
        label="resources.assets.certifications.text.preview_file"
        size="small"
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {fileName ?? translate('resources.assets.certifications.fields.documentary_proof')}
          <IconButton
            onClick={() => setOpen(false)}
            aria-label={translate('general.text.close')}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ minHeight: '70vh' }}>
          <DocumentaryProofPreview
            documentaryProof={previewSrc}
            fileName={fileName}
            mimeType={props.document.rawFile?.type}
            pdfStyle={{ width: '100%', height: '70vh' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );

};

const getAvailableRecipients = (
  recipients: DocumentAssignee[],
  assignments: Record<string, string>,
  currentIndex: number,
): DocumentAssignee[] => {

  const takenIds = new Set(
    Object.entries(assignments)
      .filter(([key]) => key !== String(currentIndex))
      .map(([, assetId]) => assetId)
      .filter(Boolean),
  );
  return recipients.filter((recipient) => !takenIds.has(recipient.id));

};

const DocumentAssignmentRow = (props: {
  document: CertificationDocument;
  index: number;
  recipients: DocumentAssignee[];
  assignments: Record<string, string>;
}) => {

  const translate = useTranslate();

  const availableRecipients = useMemo(
    () => getAvailableRecipients(props.recipients, props.assignments, props.index),
    [props.recipients, props.assignments, props.index],
  );

  const fileName = props.document.title ?? props.document.rawFile?.name;

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
    >
      <DocumentPreviewButton document={props.document} />
      <Box sx={{ flex: 1 }}>
        <GraySelectInputWithUpperLabel
          source={`assignments.${props.index}`}
          label={fileName ?? translate('resources.assets.certifications.fields.documentary_proof')}
          choices={availableRecipients}
          optionText="label"
          optionValue="id"
          validate={required()}
          fullWidth
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => {

              if (!selected || typeof selected !== 'string') {

                return (
                  <Typography component="span" color="text.secondary">
                    {translate('resources.assets.certifications.text.select_user')}
                  </Typography>
                );

              }
              const recipient = props.recipients.find((r) => r.id === selected);
              return recipient?.label ?? selected;

            },
          }}
        />
      </Box>
    </Stack>
  );

};

export const BulkDocumentAssignmentStep = (props: {
  recipients: DocumentAssignee[];
}) => {

  const translate = useTranslate();
  const { setValue } = useFormContext();
  const documents = (useWatch({ name: 'documents' }) ?? []) as CertificationDocument[];
  const assignments = useWatch({ name: 'assignments' }) as Record<string, string>;

  const hasAssignments = documents.some((_, index) => {

    const value = assignments?.[index] ?? assignments?.[String(index)];
    return Boolean(value);

  });

  const handleResetAssignments = () => {

    documents.forEach((_, index) => {

      setValue(`assignments.${index}`, null, {
        shouldDirty: true,
      });

    });

  };

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', height: 27.5 }}>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.assets.certifications.text.bulk_assign_documents_helper')}
        </Typography>
        {hasAssignments && (
          <Button
            label="resources.assets.certifications.text.reset_document_assignments"
            onClick={handleResetAssignments}
            size="small"
          />
        )}
      </Box>
      {documents.map((document, index) => (
        <DocumentAssignmentRow
          key={`${document.title ?? 'doc'}-${index}`}
          document={document}
          index={index}
          recipients={props.recipients}
          assignments={assignments}
        />
      ))}
    </Stack>
  );

};
